import { writable, get } from 'svelte/store';

export type AppView =
  | 'dashboard'
  | 'vocabulary'
  | 'practice'
  | 'settings'
  | 'flashcard-viewer'
  | 'fsrs-review'
  | 'test-mode'
  | 'test-results'
  | 'add-flashcard'
  | 'edit-flashcard'
  | 'flashcard-list'
  | 'photo-capture'
  | 'texts-list'
  | 'texts-new'
  | 'text-view'
  | 'exercise-mode'
  | 'speak';

export interface NavigationState {
  currentView: AppView;
  previousView?: AppView;
  previousParams?: Record<string, any>;
  params?: Record<string, any>;
  // Flashcard viewer state preservation
  flashcardState?: {
    index: number;
    languageId?: string;
    categoryId?: string;
  };
}

const initialState: NavigationState = {
  currentView: 'dashboard',
};

export const navigationState = writable<NavigationState>(initialState);

// Specific param interfaces
export interface FlashcardParams {
  languageId?: string;
  categoryId?: string;
}

export interface TestParams extends FlashcardParams {}

export interface TestResultParams {
  resultId: string;
}

export interface ExerciseParams extends FlashcardParams {
  /** Total number of exercises */
  totalExercises?: number;
  /** Specific exercise mode distribution */
  modeDistribution?: Partial<Record<string, number>>;
}

// Navigation actions interface
export interface NavigationActions {
  navigateTo(view: AppView, params?: Record<string, any>): void;
  goBack(): void;
  goHome(): void;
  goToVocabulary(): void;
  goToPractice(): void;
  goToSettings(): void;
  startFlashcards(languageId?: string, categoryId?: string): void;
  continueFlashcards(): void;
  saveFlashcardPosition(index: number, languageId?: string, categoryId?: string): void;
  clearFlashcardPosition(): void;
  startFSRSReview(languageId?: string, categoryId?: string): void;
  startTest(languageId?: string, categoryId?: string): void;
  startExercise(languageId?: string, categoryId?: string, config?: Partial<ExerciseParams>): void;
  showTestResults(resultId: string): void;
  manageFlashcards(): void;
  addFlashcard(): void;
  editFlashcard(cardId: string): void;
  photoCapture(): void;
  goToTexts(): void;
  addText(): void;
  viewText(textId: string): void;
  goToSpeak(): void;
}

export const navigationActions: NavigationActions = {
  // Navigate to a specific view
  navigateTo(view: AppView, params?: Record<string, any>) {
    navigationState.update((state) => ({
      previousView: state.currentView,
      previousParams: state.params,
      currentView: view,
      params,
      flashcardState: state.flashcardState, // Preserve flashcard position
    }));
  },

  // Go back to previous view
  goBack() {
    navigationState.update((state) => ({
      currentView: state.previousView || 'dashboard',
      params: state.previousParams,
      previousView: undefined,
      previousParams: undefined,
      flashcardState: state.flashcardState, // Preserve flashcard position
    }));
  },

  // Reset to dashboard
  goHome() {
    navigationState.update((state) => ({
      currentView: 'dashboard',
      previousParams: undefined,
      flashcardState: state.flashcardState, // Preserve flashcard position
    }));
  },

  // Navigate to vocabulary section
  goToVocabulary() {
    navigationActions.navigateTo('vocabulary');
  },

  // Navigate to practice/test section
  goToPractice() {
    navigationActions.navigateTo('practice');
  },

  // Navigate to settings
  goToSettings() {
    navigationActions.navigateTo('settings');
  },

  // Start flashcard viewer
  startFlashcards(languageId?: string, categoryId?: string) {
    const params: FlashcardParams = { languageId, categoryId };
    const hasParams = params.languageId !== undefined || params.categoryId !== undefined;
    navigationActions.navigateTo('flashcard-viewer', hasParams ? params : undefined);
  },

  // Start FSRS review mode
  startFSRSReview(languageId?: string, categoryId?: string) {
    const params: FlashcardParams = { languageId, categoryId };
    const hasParams = params.languageId !== undefined || params.categoryId !== undefined;
    navigationActions.navigateTo('fsrs-review', hasParams ? params : undefined);
  },

  // Start test mode
  startTest(languageId?: string, categoryId?: string) {
    const params: TestParams = { languageId, categoryId };
    const hasParams = params.languageId !== undefined || params.categoryId !== undefined;
    navigationActions.navigateTo('test-mode', hasParams ? params : undefined);
  },

  // Start exercise mode
  startExercise(languageId?: string, categoryId?: string, config?: Partial<ExerciseParams>) {
    const params: ExerciseParams = { languageId, categoryId, ...config };
    navigationActions.navigateTo('exercise-mode', params);
  },

  // Show test results
  showTestResults(resultId: string) {
    navigationActions.navigateTo('test-results', { resultId });
  },

  // Manage flashcards
  manageFlashcards() {
    navigationActions.navigateTo('flashcard-list');
  },

  // Add new flashcard
  addFlashcard() {
    navigationActions.navigateTo('add-flashcard');
  },

  // Edit flashcard
  editFlashcard(cardId: string) {
    navigationActions.navigateTo('edit-flashcard', { cardId });
  },

  // Photo capture for OCR
  photoCapture() {
    navigationActions.navigateTo('photo-capture');
  },

  // Navigate to texts list
  goToTexts() {
    navigationActions.navigateTo('texts-list');
  },

  // Add new text
  addText() {
    navigationActions.navigateTo('texts-new');
  },

  // View text
  viewText(textId: string) {
    navigationActions.navigateTo('text-view', { textId });
  },

  // Navigate to speak (voice dialogue)
  goToSpeak() {
    navigationActions.navigateTo('speak');
  },

  // Save current flashcard position
  saveFlashcardPosition(index: number, languageId?: string, categoryId?: string) {
    console.log('Saving flashcard position:', { index, languageId, categoryId });
    navigationState.update((s) => ({
      ...s,
      flashcardState: {
        index,
        languageId,
        categoryId,
      },
    }));
  },

  // Clear flashcard position
  clearFlashcardPosition() {
    navigationState.update((s) => ({
      ...s,
      flashcardState: undefined,
    }));
  },

  // Continue flashcards from saved position
  continueFlashcards() {
    const state = get(navigationState);
    console.log('continueFlashcards called, flashcardState:', state.flashcardState);
    if (state.flashcardState) {
      const { languageId, categoryId, index } = state.flashcardState;
      const params: FlashcardParams & { startIndex?: number } = {
        languageId,
        categoryId,
        startIndex: index,
      };
      console.log('Navigating to flashcard-viewer with params:', params);
      navigationActions.navigateTo('flashcard-viewer', params);
    } else {
      // No saved state, start from beginning
      console.log('No saved flashcard state, starting from beginning');
      navigationActions.startFlashcards();
    }
  },
  // Dialogue Practice
  goToDialoguePractice() {
    navigationActions.navigateTo('dialogue-practice');
  },

  startDialogueSession(config: { source: string; level: string; topicId: string; languageId: string }) {
    navigationActions.navigateTo('dialogue-session', config);
  },

  showDialogueReview(sessionId: string) {
    navigationActions.navigateTo('dialogue-review', { sessionId });
  },
};
